import { Component, type ReactNode } from "react";
import { AppErrorFallback, type AppErrorContext } from "@/components/AppErrorFallback";
import { reportError } from "@/lib/report-error";

export interface FeatureErrorBoundaryProps {
  scope: string;
  titleKey?: string;
  bodyKey?: string;
  title?: string;
  body?: string;
  context?: AppErrorContext;
  className?: string;
  errorContainerClassName?: string;
  errorContainerRole?: string;
  errorContainerAriaLabel?: string;
  extraAction?: ReactNode;
  children: ReactNode;
}

interface FeatureErrorBoundaryState {
  error: Error | null;
}

export class FeatureErrorBoundary extends Component<
  FeatureErrorBoundaryProps,
  FeatureErrorBoundaryState
> {
  constructor(props: FeatureErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): FeatureErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: { componentStack: string }): void {
    reportError(this.props.scope, error, { componentStack: info.componentStack });
  }

  private handleReset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (error) {
      const {
        titleKey,
        bodyKey,
        title,
        body,
        context,
        className,
        errorContainerClassName,
        errorContainerRole,
        errorContainerAriaLabel,
        extraAction,
      } = this.props;

      const fallback = (
        <AppErrorFallback
          variant="inline"
          context={context}
          title={title}
          body={body}
          titleKey={titleKey}
          bodyKey={bodyKey}
          error={error}
          onReset={this.handleReset}
          className={className}
          extraAction={extraAction}
        />
      );

      if (errorContainerClassName) {
        return (
          <div
            className={
              errorContainerClassName === "ai-chat-panel"
                ? "ai-chat-panel ai-chat-panel--error"
                : errorContainerClassName
            }
            role={errorContainerRole}
            aria-label={errorContainerAriaLabel}
          >
            {fallback}
          </div>
        );
      }

      return fallback;
    }

    return this.props.children;
  }
}
